import {
  RendererEvent,
  RendererEventType,
  RendererEventTypeMap,
  RendererHostClientStub,
} from '@fullstory/renderer-shared';

export type RendererEventListenerCallback<E extends RendererEventType> = (
  e: RendererEventTypeMap[E],
) => void;

type RendererEventListeners = {
  [E in RendererEventType]: Set<RendererEventListenerCallback<E>>;
};

/**
 * RendererEventDispatcher is a RendererHost implementation that manages event
 * listeners for renderer events and dispatches incoming events to the
 * appropriate listeners.
 */
export class RendererEventDispatcher extends RendererHostClientStub {
  #eventListeners: RendererEventListeners = {
    keydown: new Set(),
    keyup: new Set(),
    observation: new Set(),
    pointerdown: new Set(),
    pointerup: new Set(),
    pointermove: new Set(),
    pointerover: new Set(),
    pointerout: new Set(),
    resize: new Set(),
    scrollend: new Set(),
    scrollstart: new Set(),
  };

  addEventListener = <E extends RendererEventType>(
    event: E,
    listener: RendererEventListenerCallback<E>,
  ): void => {
    // TODO(sethf): TypeScript limitations force us to use 'any' in these
    // methods because E could be a union of multiple event types, and we can't
    // express that we want to allow only one. That limitation will be removed
    // if existential types are ever added to TypeScript; see e.g. this issue:
    //   https://github.com/microsoft/TypeScript/issues/55257
    this.#eventListeners[event].add(listener as any);
  };

  removeEventListener = <E extends RendererEventType>(
    event: E,
    listener: RendererEventListenerCallback<E>,
  ): void => {
    this.#eventListeners[event].delete(listener as any);
  };

  onEvent = (event: RendererEvent) => {
    for (const listener of this.#eventListeners[event.type]) {
      listener(event as any);
    }
  };
}
